import { useMemo } from "react";
import { Link, useParams, Redirect } from "wouter";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ExternalLink, Quote, Target, Lightbulb, BarChart3, Wrench } from "lucide-react";
import { useSEO } from "@/hooks/use-seo";
import { caseTitle, caseDescription } from "@shared/seo-routes";
import { useLanguage } from "@/contexts/LanguageContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ExpertContact from "@/components/ExpertContact";
import { cases } from "@/data/cases";

export default function CaseDetail() {
  const { slug } = useParams<{ slug: string }>();
  const { language, localePath } = useLanguage();
  const nl = language === "nl";

  const caseData = useMemo(() => cases.find((c) => c.slug === slug), [slug]);

  const otherCases = useMemo(() => {
    if (!caseData) return [];
    return cases.filter((c) => c.slug !== slug);
  }, [slug, caseData]);

  const caseIndex = cases.findIndex((c) => c.slug === slug);
  const prevCase = caseIndex > 0 ? cases[caseIndex - 1] : cases[cases.length - 1];
  const nextCase = caseIndex < cases.length - 1 ? cases[caseIndex + 1] : cases[0];

  const caseKeywords = caseData
    ? [
        caseData.name,
        ...caseData.services,
        caseData.vertical,
        nl ? 'digital marketing bureau amsterdam' : 'digital marketing agency amsterdam',
        nl ? 'D2C e-commerce marketing' : 'D2C e-commerce marketing',
        nl ? 'performance marketing resultaten' : 'performance marketing results',
        ...caseData.channels.slice(0, 4),
      ].join(', ')
    : '';

  useSEO({
    title: caseData ? caseTitle(caseData.name) : "Case | Wrkt Digital",
    description: caseData ? caseDescription(caseData.name, nl ? "nl" : "en") : "",
    path: `/cases/${slug}`,
    lang: nl ? "nl" : "en",
    keywords: caseKeywords,
  });

  if (!caseData) {
    return <Redirect to="/cases" />;
  }

  return (
    <div className="min-h-screen bg-[#0A1F14] text-white relative">
      <div className="relative z-10">
      <Navbar activePage="cases" />



      <style>{`
        @keyframes color-pulse-shift {
          0% { background-color: rgba(141, 24, 43, 0.5); transform: scale(1); opacity: 0.5; }
          50% { background-color: rgba(76, 234, 172, 0.2); transform: scale(1.04); opacity: 0.35; }
          100% { background-color: rgba(141, 24, 43, 0.5); transform: scale(1); opacity: 0.5; }
        }
      `}</style>

      <section className="relative pt-28 md:pt-36 pb-16 overflow-hidden bg-grad-hero has-grain breathing">

        <div className="container mx-auto px-6 md:px-12 relative z-10">
          <Link href={localePath("/cases")} className="inline-flex items-center gap-2 text-sm text-white/50 hover:text-[#4CEAAC] transition-colors mb-8" data-testid="link-back-cases">
            <ArrowLeft className="w-4 h-4" />
            {nl ? "Alle cases" : "All cases"}
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1 bg-[#256C43]/40 rounded-full border border-[#4CEAAC]/20 text-[10px] font-semibold uppercase tracking-widest text-[#4CEAAC]">
                  {caseData.vertical}
                </span>
                {caseData.website && (
                  <a
                    href={caseData.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs text-white/40 hover:text-[#4CEAAC] transition-colors"
                    data-testid="link-case-website"
                  >
                    <ExternalLink className="w-3 h-3" />
                    Website
                  </a>
                )}
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold leading-[1.04] tracking-[-0.03em] mb-4">
                {caseData.name}.
              </h1>
              <p className="text-xl md:text-2xl text-white/60 font-light leading-relaxed mb-8">
                {nl ? caseData.tagline.nl : caseData.tagline.en}
              </p>

              <div className="grid grid-cols-3 gap-6">
                {caseData.results.map((r) => (
                  <div key={r.value} className="text-center md:text-left">
                    <div className="text-3xl md:text-4xl font-display font-extrabold text-[#4CEAAC]">
                      {r.value}
                    </div>
                    <div className="text-xs uppercase tracking-wider text-white/40 mt-1">
                      {nl ? r.label.nl : r.label.en}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="relative rounded-2xl overflow-hidden aspect-[4/3]"
            >
              <img
                src={caseData.heroImage}
                alt={`${caseData.name} case Wrkt Digital`}
                className="w-full h-full object-cover"
                style={caseData.imagePosition ? { objectPosition: caseData.imagePosition } : undefined}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A1F14]/30 via-transparent to-transparent" />
            </motion.div>
          </div>
        </div>
      </section>

      {caseData.timeline && (
        <section className="py-16 md:py-24 border-t border-white/5">
          <div className="container mx-auto px-6 md:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="lg:col-span-7 rounded-2xl border border-white/10 bg-white/[0.03] p-8 md:p-10"
              >
                {caseData.resultsTitle && (
                  <h2 className="text-2xl md:text-3xl font-display font-bold mb-10">
                    {nl ? caseData.resultsTitle.nl : caseData.resultsTitle.en}
                  </h2>
                )}
                <ol className="relative border-l border-[#4CEAAC]/20 pl-8 space-y-8">
                  {caseData.timeline.map((t2, i) => (
                    <li key={i} className="relative">
                      <span className="absolute -left-8 top-1 h-3 w-3 -translate-x-1/2 rounded-full bg-[#4CEAAC] shadow-[0_0_12px_rgba(76,234,172,0.6)]"></span>
                      <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#4CEAAC]">{t2.marker}</span>
                      <h3 className="mt-1 font-display font-bold text-lg">{nl ? t2.title.nl : t2.title.en}</h3>
                      <p className="mt-1 text-sm text-white/60 leading-relaxed">{nl ? t2.text.nl : t2.text.en}</p>
                    </li>
                  ))}
                </ol>
              </motion.div>
              <div className="lg:col-span-5 space-y-6">
                {caseData.results.map((r, i) => (
                  <motion.div
                    key={r.value}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: i * 0.1 }}
                    className="rounded-2xl border border-white/10 bg-white/[0.03] p-8"
                  >
                    <div className="text-4xl md:text-5xl font-display font-extrabold text-[#4CEAAC] [text-shadow:0_0_32px_rgba(76,234,172,0.3)]">{r.value}</div>
                    <div className="mt-2 text-xs uppercase tracking-[0.16em] text-white/50 font-semibold">{nl ? r.label.nl : r.label.en}</div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      <section className="py-16 md:py-24">
        <div className="container mx-auto px-6 md:px-12">
          <div className={`grid grid-cols-1 gap-8 md:gap-12 ${caseData.timeline ? 'lg:grid-cols-2' : 'lg:grid-cols-3'}`}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="bg-white/[0.03] border border-white/10 rounded-2xl p-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-[#256C43]/30 border border-[#4CEAAC]/20 flex items-center justify-center">
                  <Target className="w-5 h-5 text-[#4CEAAC]" />
                </div>
                <h2 className="text-lg font-display font-bold">
                  {nl ? "Doelen" : "Goals"}
                </h2>
              </div>
              <ul className="space-y-3">
                {(nl ? caseData.goals.nl : caseData.goals.en).map((g, i) => (
                  <li key={i} className="flex gap-3 text-sm text-white/70 leading-relaxed">
                    <span className="text-[#4CEAAC] mt-1 shrink-0">-</span>
                    {g}
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-white/[0.03] border border-white/10 rounded-2xl p-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-[#256C43]/30 border border-[#4CEAAC]/20 flex items-center justify-center">
                  <Lightbulb className="w-5 h-5 text-[#4CEAAC]" />
                </div>
                <h2 className="text-lg font-display font-bold">
                  {nl ? "Aanpak" : "Approach"}
                </h2>
              </div>
              <ul className="space-y-3">
                {(nl ? caseData.approach.nl : caseData.approach.en).map((a, i) => (
                  <li key={i} className="flex gap-3 text-sm text-white/70 leading-relaxed">
                    <span className="text-[#4CEAAC] mt-1 shrink-0">-</span>
                    {a}
                  </li>
                ))}
              </ul>
            </motion.div>

            {!caseData.timeline && <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-white/[0.03] border border-white/10 rounded-2xl p-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-[#256C43]/30 border border-[#4CEAAC]/20 flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-[#4CEAAC]" />
                </div>
                <h2 className="text-lg font-display font-bold">
                  {nl ? "Resultaten" : "Results"}
                </h2>
              </div>
              <div className="space-y-4">
                {caseData.results.map((r) => (
                  <div key={r.value} className="flex items-center justify-between border-b border-white/5 pb-3 last:border-0">
                    <span className="text-sm text-white/60">
                      {nl ? r.label.nl : r.label.en}
                    </span>
                    <span className="text-lg font-display font-bold text-[#4CEAAC]">
                      {r.value}
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 border-t border-white/10">
        <div className="container mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-7"
            >
              <h2 className="text-2xl md:text-3xl font-display font-bold mb-8">
                {nl ? "Het verhaal." : "The story."}
              </h2>
              <div className="text-white/70 leading-[1.7] space-y-6 text-[16.5px]">
                {(caseData.story
                  ? (nl ? caseData.story.nl : caseData.story.en)
                  : (nl ? caseData.description.nl : caseData.description.en).split("\n\n")
                ).map((paragraph, i) => (
                  <p key={i}>{paragraph}</p>
                ))}
              </div>

              {caseData.quote && (
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className="mt-12 relative bg-white/[0.03] border border-white/10 rounded-2xl p-8"
                >
                  <Quote className="w-8 h-8 text-[#4CEAAC]/30 absolute top-6 right-6" />
                  <blockquote className="text-white/80 italic leading-relaxed mb-4 text-[15px]">
                    "{nl ? caseData.quote.text.nl : caseData.quote.text.en}"
                  </blockquote>
                  <div className="flex items-center gap-3">
                    {caseData.quote.image ? (
                      <img src={caseData.quote.image} alt={caseData.quote.author} loading="lazy" className="h-10 w-10 shrink-0 rounded-full object-cover border border-[#4CEAAC]/30" />
                    ) : (
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#256C43]/40 border border-[#4CEAAC]/30 text-xs font-bold text-[#4CEAAC]">
                        {caseData.quote.author.split(" ").map((w) => w[0]).slice(0, 2).join("")}
                      </span>
                    )}
                    <div>
                      <p className="font-semibold text-sm">{caseData.quote.author}</p>
                      <p className="text-xs text-white/40">{caseData.quote.role}</p>
                    </div>
                  </div>
                </motion.div>
              )}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="lg:col-span-5"
            >
              <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-8 sticky top-32">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-[#256C43]/30 border border-[#4CEAAC]/20 flex items-center justify-center">
                    <Wrench className="w-5 h-5 text-[#4CEAAC]" />
                  </div>
                  <h3 className="text-lg font-display font-bold">
                    {nl ? "Kanalen & Tools" : "Channels & Tools"}
                  </h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  {caseData.channels.map((ch) => (
                    <span
                      key={ch}
                      className="px-3 py-1.5 bg-[#256C43]/20 border border-[#4CEAAC]/10 rounded-full text-xs font-medium text-white/70"
                    >
                      {ch}
                    </span>
                  ))}
                </div>

                <div className="mt-8 pt-8 border-t border-white/10">
                  <p className="text-sm text-white/40 mb-4">
                    {nl
                      ? "Wil je dezelfde resultaten voor jouw merk?"
                      : "Want the same results for your brand?"}
                  </p>
                  <Link href={localePath("/contact")}>
                    <button
                      data-testid="button-case-cta"
                      className="btn btn--gradient btn--lg w-full"
                    >
                      {nl ? "Plan een kennismaking" : "Book an intro call"}
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </Link>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>


      <section className="py-8 border-t border-white/10">
        <div className="container mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between">
            <Link
              href={localePath(`/cases/${prevCase.slug}`)}
              className="group flex items-center gap-3 py-6 text-white/50 hover:text-white transition-colors"
              data-testid="link-prev-case"
            >
              <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
              <div className="hidden md:block">
                <div className="text-xs uppercase tracking-wider text-white/30 mb-1">
                  {nl ? "Vorige case" : "Previous case"}
                </div>
                <div className="font-display font-semibold">{prevCase.name}</div>
              </div>
            </Link>

            <Link
              href={localePath("/cases")}
              className="text-xs uppercase tracking-widest text-white/30 hover:text-[#4CEAAC] transition-colors font-semibold"
              data-testid="link-all-cases"
            >
              {nl ? "Alle cases" : "All cases"}
            </Link>

            <Link
              href={localePath(`/cases/${nextCase.slug}`)}
              className="group flex items-center gap-3 py-6 text-white/50 hover:text-white transition-colors"
              data-testid="link-next-case"
            >
              <div className="hidden md:block text-right">
                <div className="text-xs uppercase tracking-wider text-white/30 mb-1">
                  {nl ? "Volgende case" : "Next case"}
                </div>
                <div className="font-display font-semibold">{nextCase.name}</div>
              </div>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 border-t border-white/10">
        <div className="container mx-auto px-6 md:px-12">
          <h2 className="text-2xl md:text-3xl font-display font-bold mb-10">
            {nl ? "Meer cases" : "More cases"}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {otherCases.slice(0, 3).map((c, i) => (
              <Link key={c.slug} href={localePath(`/cases/${c.slug}`)} data-testid={`card-related-${c.slug}`}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.1 }}
                  className="group bg-white/[0.03] border border-white/10 rounded-2xl overflow-hidden hover:border-[#4CEAAC]/30 transition-all duration-500 cursor-pointer"
                >
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <img
                      src={c.heroImage}
                      alt={`${c.name} case Wrkt Digital`}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      style={c.imagePosition ? { objectPosition: c.imagePosition } : undefined}
                      loading="lazy"
                      decoding="async"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A1F14] via-transparent to-transparent opacity-60" />
                  </div>
                  <div className="p-5">
                    <h3 className="font-display font-bold group-hover:text-[#4CEAAC] transition-colors mb-1">
                      {c.name}
                    </h3>
                    <p className="text-sm text-white/40">
                      {nl ? c.tagline.nl : c.tagline.en}
                    </p>
                  </div>
                </motion.div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <ExpertContact />

      <Footer />
      </div>
    </div>
  );
}
